import React from 'react';

import { UnhandledError } from './application/errors/UnhandledError';
import { AppBuilder, OfflineAppBuilder } from './application/features/App/components/AppBuilder';
import { Logger } from './application/Logging/Logger';
import { getStudentViewModeFromString, StudentViewMode } from './config/constants';
import { dynamicEnv } from './config/dynamic/dynamicEnv';
import { throwOnUnset } from './global/utils/throwOnUnset';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet';
import { polyfill } from 'smoothscroll-polyfill';


// Based on https://stackoverflow.com/questions/51229742/javascript-window-scroll-behavior-smooth-not-working-in-safari
polyfill();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      gcTime: Infinity,
      networkMode: 'always',
    },
  },
});

function trimSlash(s: string) {
  while (s.endsWith('/')) {
    s = s.slice(0, -1);
  }
  return s;
}

const ApplicationEntryPoint = () => {
  const viewModeString = throwOnUnset(dynamicEnv("VIEW_MODE"), "VIEW_MODE not set, cannot use app without it.");
  const backendUrl = trimSlash(throwOnUnset(dynamicEnv("BACKEND_URL"), "BACKEND_URL not set, cannot use app without it."));
  const isPanomioString = throwOnUnset(dynamicEnv("IS_PANOMIO"), "IS_PANOMIO not set, cannot use app without it.");

  const viewMode = getStudentViewModeFromString(viewModeString);
  const isPanomio = isPanomioString === "true";
  const queryParams = getStartupQueryParams();

  return (
    <React.StrictMode>
      <ErrorBoundary
        FallbackComponent={UnhandledError}
        onReset={() => window.location.reload()}
        onError={(error) => {
          Logger.trackException(error);
        }}
      >
        <Helmet>
          {/* TODO add light/dark theme */}
          {/* <html className="light" /> */}
        </Helmet>
        <QueryClientProvider client={queryClient}>
          {
            (viewMode === StudentViewMode.Basic ||
            viewMode === StudentViewMode.SCORM ||
            viewMode === StudentViewMode.Public || 
            viewMode === StudentViewMode.Preview) &&
            <AppBuilder isInPreview={queryParams.isInPreview} viewMode={viewMode} backendUrl={backendUrl} isPanomio={isPanomio} hideStudentHeader={queryParams.isStudentHeaderHidden} />
          }
          {
            (viewMode === StudentViewMode.Offline_SCORM ||
             viewMode === StudentViewMode.Offline_TinCan ||
             viewMode === StudentViewMode.ExportedPackaged_NullClient ||
             viewMode === StudentViewMode.TinCan ||
             viewMode === StudentViewMode.Offline) &&
              <OfflineAppBuilder viewMode={viewMode} backendUrl={backendUrl} isPanomio={isPanomio} />
          }
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
};

class App extends React.Component {
  render() {
    return <ApplicationEntryPoint />;
  }
}

export default App;

const getStartupQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  const isInPreviewString = params.get("preview") ?? ""; // will be false
  const hideStudentHeaderString = params.get("hideStudentHeader") ?? ""; // will be false
  const cultureCodeString = params.get("cultureCode");
  return {
    isInPreview: Boolean(isInPreviewString),
    isStudentHeaderHidden: Boolean(hideStudentHeaderString),
    cultureCode: cultureCodeString
  };
};
